import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import eventBus from "../utils/EventBus";
import { generatePrintUrl } from "../utils/PrintSettingsUtils";
import { 
  FileText as FileTextIcon, 
  Folder as FolderIcon, 
  List as ListIcon, 
  User as UserIcon,
  Users as UsersIcon,
  FilePlus as FilePlusIcon,
  Save as SaveIcon,
  Import as ImportIcon,
  Upload as UploadIcon,
  FolderPlus as FolderPlusIcon,
  ListPlus,
  Edit,
  ExternalLink,
  Download,
  Plus as PlusIcon,
  Youtube,
  Trash as TrashIcon,
  LogOut as LogOutIcon,
  Calendar as CalendarIcon
} from "lucide-react";
import SidebarButton from "../components/SidebarButton";
import ImportModal from "../components/ImportModal";
import ExportModal from "../components/ExportModal";
import { exportSingleSheet } from "../services/ExportService";
import VersionMenu from "../components/VersionMenu";
import { useAuth } from "../contexts/AuthContext";
import { useSetlistActions, useSheetActions } from "../contexts/ActionsContext";
import { useSheetData } from "../contexts/SheetDataContext";
import logo from "../assets/logo.png";

/**
 * Main application layout with header and sidebar
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Child components
 * @param {Function} [props.handleNewSheet] Function to create a new sheet
 * @param {Function} [props.handleImport] Function to import sheets
 * @param {Function} [props.handleExport] Function to export sheets
 */
const AppLayout = ({ children }) => {
  // State for modals
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const location = useLocation();
  const { currentUser, isAuthenticated, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("sheets");
  const [isEditor, setIsEditor] = useState(false);
  const [isSetlists, setIsSetlists] = useState(false);
  const [isSheets, setIsSheets] = useState(false);
  const [isBands, setIsBands] = useState(false);
  const [isBandPage, setIsBandPage] = useState(false);
  const [isProfilePage, setIsProfilePage] = useState(false);
  const navigate = useNavigate();
  const { setlistActions } = useSetlistActions();
  const { handleCreateSheet } = useSheetActions();
  const { songData, currentSheetId } = useSheetData();
  
  // Handler for exporting a single sheet
  const handleExportSheet = async (sheetId) => {
    try {
      const result = await exportSingleSheet(sheetId);
      console.log('Sheet exported:', result.message);
    } catch (error) {
      console.error('Export failed:', error.message);
    }
  };
  
  // Set up event listeners for import/export functionality
  useEffect(() => {
    console.log('Setting up event listeners in AppLayout');
    
    // Add event listeners with explicit debug logging
    const importListener = eventBus.on('app:import', () => {
      console.log('*** Import event received in AppLayout ***');
      setImportModalOpen(true);
    });

    const exportListener = eventBus.on('app:export', () => {
      console.log('*** Export event received in AppLayout ***');
      setExportModalOpen(true);
    });
    
    // Log all registered event listeners for debugging
    console.log('Current event listeners:', eventBus.listeners);

    // Clean up
    return () => {
      console.log('Cleaning up event listeners in AppLayout');
      importListener();
      exportListener();
    };
  }, []);

  // Set active tab based on current route and check if we're in the editor
  useEffect(() => {
    // Check if we're in the editor view (single sheet editor)
    const inEditor = location.pathname.match(/\/sheet\/[a-zA-Z0-9_-]+/);
    const isInEditor = !!inEditor;
    console.log('Current path:', location.pathname, 'isEditor:', isInEditor);
    setIsEditor(isInEditor);
    
    // Check if we're in the setlists view or viewing a specific setlist
    const inSetlists = location.pathname === "/setlists" || (location.pathname.includes('/setlist/') && !location.pathname.includes('/setlists'));
    console.log('Current path:', location.pathname, 'isSetlists:', inSetlists);
    setIsSetlists(inSetlists);
    
    // Check if we're in the sheets view
    const inSheets = location.pathname === "/sheets";
    console.log('Current path:', location.pathname, 'isSheets:', inSheets);
    setIsSheets(inSheets);
    
    // Check if we're in the bands view
    const inBands = location.pathname === "/bands";
    console.log('Current path:', location.pathname, 'isBands:', inBands);
    setIsBands(inBands);
    
    // Check if we're in an individual band page
    const inBandPage = location.pathname.match(/\/band\/[a-zA-Z0-9_-]+/);
    const isInBandPage = !!inBandPage;
    console.log('Current path:', location.pathname, 'isBandPage:', isInBandPage);
    setIsBandPage(isInBandPage);
    
    // Check if we're in the profile page
    const inProfilePage = location.pathname.match(/\/profile/);
    const isInProfilePage = !!inProfilePage;
    console.log('Current path:', location.pathname, 'isProfilePage:', isInProfilePage);
    setIsProfilePage(isInProfilePage);
    
    // Set active tab - but more specific now to differentiate sheet editor from sheets list
    if (location.pathname === "/sheets") {
      // Only the sheets listing page, not the editor
      setActiveTab("sheets");
    } else if (location.pathname.includes("/setlists") || location.pathname.includes("/setlist/")) {
      setActiveTab("setlists");
    } else if (location.pathname === "/bands") {
      setActiveTab("bands");
    } else if (location.pathname.includes("/band/")) {
      // Individual band page - highlight bands tab
      setActiveTab("bands");
    } else if (location.pathname === "/events" || location.pathname.includes("/event/")) {
      setActiveTab("events");
    } else if (location.pathname.includes("/sheet/")) {
      // In editor - don't highlight any main tab
      setActiveTab("none");
    }
  }, [location.pathname]);

  const handleLoginClick = () => {
    navigate('/signin');
  };

  const handleLogoutClick = () => {
    logout();
  };


  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header - fixed at the top */}
      <div className="fixed top-0 z-50 flex flex-col border-b border-border bg-card w-full bg-background">
        {/* Main header row */}
        <header className="h-14 flex items-center justify-between px-4 w-full">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img src={logo} alt="Band Sheets" className="h-8 w-auto" />
              <span className="text-primary text-xl font-bold">Band Sheets</span>
            </Link>
            
          </div>

          <div className="flex items-center space-x-2">
            
            {/* Main Navigation - visible on both mobile and desktop */}
            <nav className="flex space-x-1 mr-2">
              {/* Navigation tabs - always visible */}
              <SidebarButton
                icon={FileTextIcon}
                active={activeTab === "sheets"}
                label="Sheets"
                to="/sheets"
                onClick={() => setActiveTab("sheets")}
                variant="horizontal"
              />

              <SidebarButton
                icon={FolderIcon}
                active={activeTab === "setlists"}
                label="Setlists"
                to="/setlists"
                onClick={() => setActiveTab("setlists")}
                variant="horizontal"
              />
              
              <SidebarButton
                icon={UsersIcon}
                active={activeTab === "bands"}
                label="Bands"
                to="/bands"
                onClick={() => setActiveTab("bands")}
                variant="horizontal"
              />
              
              <SidebarButton
                icon={CalendarIcon}
                active={activeTab === "events"}
                label="Events"
                to="/events"
                onClick={() => setActiveTab("events")}
                variant="horizontal"
              />
            </nav>
            
            
            {/* Account Button */}
            <div className="relative">
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/profile')}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors"
                  title="Go to Profile"
                >
                  <UserIcon className="w-3 h-3" />
                  <span className="hidden sm:inline">{currentUser?.username || 'Account'}</span>
                </button>
              ) : (
                <button
                  onClick={handleLoginClick}
                  className="text-muted-foreground hover:text-foreground hover:bg-muted px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors"
                >
                  <UserIcon className="w-3 h-3" />
                  <span className="hidden sm:inline">Login</span>
                </button>
              )}
            </div>
          </div>
        </header>

      </div>

      {/* Main content area with content */}
      <div className="flex flex-1 mt-14 pb-20">

        {/* Content area - natural scrolling */}
        <main className="flex-1 w-full">{children}</main>
        
        {/* Auth Modal */}
        
        {/* Import/Export Modals */}
        <ImportModal 
          isOpen={importModalOpen} 
          onClose={() => setImportModalOpen(false)}
          onSuccess={() => {
            setImportModalOpen(false);
            // Refresh sheet list after successful import
            if (location.pathname === '/sheets') {
              window.location.reload();
            }
          }}
        />
        <ExportModal 
          isOpen={exportModalOpen} 
          onClose={() => setExportModalOpen(false)} 
        />
      </div>
    </div>
  );
};

export default AppLayout;
